import type { DistanceReadingSource } from '@chukua/common';
import type { ReactNode } from 'react';
import type { DistanceTripDetail } from '../distance-agreement.api';
import { NOT_COMPARABLE_LABELS, formatKm, formatPercent, odometerUnavailable } from '../policies/distance-labels.policy';

const when = (iso: string) => new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

function SourceCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="flex flex-col gap-1 rounded-lg border border-line bg-surface p-4">
      <h3 className="text-sm font-semibold text-fg">{title}</h3>
      {children}
    </section>
  );
}

function ReadingSource({ title, reading, missing }: { title: string; reading: DistanceReadingSource | null; missing: string }) {
  if (!reading) {
    return (
      <SourceCard title={title}>
        <p className="text-muted">Not available</p>
        <p className="text-sm text-muted">{missing}</p>
      </SourceCard>
    );
  }
  return (
    <SourceCard title={title}>
      <p className="text-lg font-medium tabular-nums">{reading.km.toLocaleString('en-US')} km</p>
      <p className="text-sm text-muted">
        {reading.byName ? `Recorded by ${reading.byName}` : "Recorded on the driver's phone"} · {when(reading.at)}
      </p>
    </SourceCard>
  );
}

function GpsSource({ trip }: { trip: DistanceTripDetail }) {
  if (trip.gpsDistanceKm === null) {
    return (
      <SourceCard title="GPS distance">
        <p className="text-muted">Not available</p>
        <p className="text-sm text-muted">{NOT_COMPARABLE_LABELS.no_gps_distance}</p>
      </SourceCard>
    );
  }
  return (
    <SourceCard title="GPS distance">
      <p className="text-lg font-medium tabular-nums">{formatKm(trip.gpsDistanceKm)}</p>
      <p className="text-sm text-muted">
        {Math.round(trip.gpsCoveragePct ?? 0)}% of the trip covered{trip.isGpsLowCoverage ? ': a lower bound, so it cannot flag a gap' : ''}
      </p>
      {trip.gpsComputedAt && <p className="text-sm text-muted">Worked out {when(trip.gpsComputedAt)}</p>}
    </SourceCard>
  );
}

function GapSummary({ trip }: { trip: DistanceTripDetail }) {
  const odometerMissing = odometerUnavailable(trip);
  return (
    <SourceCard title="Comparison">
      {trip.gapKm === null ? (
        <>
          <p className="text-muted">Not available</p>
          <p className="text-sm text-muted">{odometerMissing ?? NOT_COMPARABLE_LABELS.no_gps_distance}</p>
        </>
      ) : (
        <>
          <p className="text-lg font-medium tabular-nums">
            {formatKm(trip.gapKm)}
            {trip.gapPercent !== null && ` (${formatPercent(trip.gapPercent)})`}
          </p>
          <p className="text-sm text-muted">Odometer distance {trip.odometerDistanceKm === null ? 'not available' : formatKm(trip.odometerDistanceKm)}</p>
        </>
      )}
      <p className="text-sm text-muted">Tolerance when this was judged: {trip.tolerancePercent}%</p>
      {trip.authoritativeDistanceKm !== null && (
        <p className="text-sm text-fg">Distance used from here: {formatKm(trip.authoritativeDistanceKm)} (the odometer's)</p>
      )}
    </SourceCard>
  );
}

const TRUCK_WRITE_TEXT = {
  written: "The close-out reading is now the truck's odometer.",
  below_current: "The close-out reading was below the truck's odometer, so the truck was left as it was.",
} as const;

/** Where each figure of a trip's comparison came from: the two readings and who took them, the GPS figure, and what the truck's odometer did with it. */
export function DistanceSources({ trip }: { trip: DistanceTripDetail }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <ReadingSource title="Dispatch reading" reading={trip.dispatchReading} missing="The truck had no pre-trip check before it left." />
        <ReadingSource title="Close-out reading" reading={trip.closeOutReading} missing="The truck was not received at the yard with a reading." />
        <GpsSource trip={trip} />
        <GapSummary trip={trip} />
      </div>
      <p className="text-sm text-muted">{trip.truckWrite ? TRUCK_WRITE_TEXT[trip.truckWrite] : "The truck's odometer has not taken this reading yet."}</p>
    </div>
  );
}
